'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useFrame, useThree } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { anchorXrKeyboardWindow, followXrBody, resolveXrAnchoredWindow, resolveXrHeadYaw, resolveXrKeyboardWindow, XR_KEYBOARD_WINDOW } from '../_pure/xrView.js';
import { spaceCanvasXrBindings } from '../xrBindings.js';
import { useXrKeyBinding } from './XrBindings.js';
const VIEW_LABELS = {
    virtual: 'View: virtual', keyboard: 'View: keyboard window (K re-places it)', passthrough: 'View: passthrough',
};
const NEXT_VIEW = { virtual: 'keyboard', keyboard: 'passthrough', passthrough: 'virtual' };
const OWNER = 'XR view';
// The chosen view outlives a session; the window placement does not, because each session has its own tracking origin.
let viewMode = 'virtual';
const HOLE_VERTEX = `varying vec2 vUv;
void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;
// Outputs the factor the framebuffer is multiplied by: `dim` inside (mostly camera, tinted by the backdrop colour),
// fading to 1 (unchanged) over the soft rim.
const HOLE_FRAGMENT = `varying vec2 vUv;
uniform vec2 size;
uniform float soft;
uniform float dim;
void main() {
    vec2 inset = min(vUv, 1.0 - vUv) * size;
    gl_FragColor = vec4(0.0, 0.0, 0.0, mix(dim, 1.0, smoothstep(0.0, soft, soft - min(inset.x, inset.y))));
}`;
/**
 * Mixed-reality sessions composite the room camera wherever the frame is transparent. This keeps the scene fully
 * virtual behind an opaque backdrop, opens a dimmed soft-edged window onto the keyboard, or shows the whole room.
 * Rendered inside the XR origin, so locomotion never moves the window; it follows the body when the viewer turns or
 * shifts for a while, keeping the offset captured by K.
 */
export default function XrPassthroughView() {
    const session = useXR(state => state.session);
    const mixed = session?.environmentBlendMode === 'alpha-blend';
    const head = useThree(state => state.gl.xr.getCamera());
    const scene = useThree(state => state.scene);
    const [mode, setMode] = useState(viewMode);
    const [placed, setPlaced] = useState(false);
    const anchor = useRef(undefined);
    const backdrop = useRef(null);
    const windowMesh = useRef(null);
    const backdropColor = useMemo(() => new THREE.Color('black'), []);
    const hole = useMemo(() => new THREE.ShaderMaterial({
        vertexShader: HOLE_VERTEX, fragmentShader: HOLE_FRAGMENT, side: THREE.DoubleSide,
        uniforms: { size: { value: new THREE.Vector2(XR_KEYBOARD_WINDOW.width, XR_KEYBOARD_WINDOW.depth) },
            soft: { value: XR_KEYBOARD_WINDOW.soft }, dim: { value: XR_KEYBOARD_WINDOW.dim } },
        blending: THREE.CustomBlending, blendEquation: THREE.AddEquation,
        blendSrc: THREE.ZeroFactor, blendDst: THREE.SrcAlphaFactor,
        blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.SrcAlphaFactor,
    }), []);
    useEffect(() => () => hole.dispose(), [hole]);
    useEffect(() => {
        anchor.current = undefined;
        setPlaced(false);
        if (session && viewMode === 'keyboard')
            spaceCanvasXrBindings.notify('Look down at your keyboard and press K');
    }, [session]);
    const place = () => {
        const next = resolveXrKeyboardWindow(head.position, head.quaternion);
        anchor.current = anchorXrKeyboardWindow(next, head.position, next.yaw);
        setPlaced(true);
        return next;
    };
    const show = (next) => {
        viewMode = next;
        setMode(next);
        if (next === 'keyboard' && !anchor.current)
            place();
        spaceCanvasXrBindings.notify(VIEW_LABELS[next]);
    };
    const unavailable = () => spaceCanvasXrBindings.notify('Passthrough needs a mixed-reality session; exit and enter XR again.');
    useXrKeyBinding('KeyP', session ? { owner: OWNER, key: 'P', legend: 'view: virtual / keyboard window / passthrough',
        onKey: () => { if (mixed)
            show(NEXT_VIEW[mode]);
        else
            unavailable(); } } : undefined);
    useXrKeyBinding('KeyK', session ? { owner: OWNER, key: 'K', legend: 'look at your keyboard: place its window here',
        onKey: () => {
            if (!mixed) {
                unavailable();
                return;
            }
            const placed = place();
            viewMode = 'keyboard';
            setMode('keyboard');
            spaceCanvasXrBindings.notify(placed.aimed ? 'Keyboard window placed'
                : 'Placed below you - look down at the keyboard and press K to aim it');
        } } : undefined);
    useFrame((_, delta) => {
        const current = anchor.current;
        if (current) {
            current.body = followXrBody(current.body, head.position, resolveXrHeadYaw(head.quaternion), delta);
            const mesh = windowMesh.current;
            if (mesh) {
                const pose = resolveXrAnchoredWindow(current);
                mesh.position.fromArray(pose.position);
                mesh.rotation.set(-Math.PI / 2, pose.yaw, 0, 'YXZ');
            }
        }
        const mesh = backdrop.current;
        if (!mesh)
            return;
        mesh.position.copy(head.position);
        const background = scene.background;
        mesh.material.color.copy(background?.isColor ? background : backdropColor);
    });
    if (!session || !mixed || mode === 'passthrough')
        return null;
    return _jsxs(_Fragment, { children: [_jsxs("mesh", { ref: backdrop, renderOrder: -100000, frustumCulled: false, raycast: () => null, children: [_jsx("sphereGeometry", { args: [1, 24, 16] }), _jsx("meshBasicMaterial", { side: THREE.BackSide, depthTest: false, depthWrite: false, toneMapped: false })] }), mode === 'keyboard' && placed ? _jsx("mesh", { ref: windowMesh, material: hole, renderOrder: -99999, frustumCulled: false, raycast: () => null, children: _jsx("planeGeometry", { args: [XR_KEYBOARD_WINDOW.width, XR_KEYBOARD_WINDOW.depth] }) }) : null] });
}
